import { type MutableRefObject, useRef, useState } from 'react';
import { ICE_GATHERING_TIMEOUT, SDP_ANSWER_TIMEOUT } from '../constants';
import { useIceConnectedTimeout } from './useIceConnectedTimeout';
import type { AvatarState } from '../types';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

export interface UseAvatarPeerConnectionOptions {
  /** Authenticated user id (telemetry only). */
  userId: string;
  /** Telemetry sink (see useVoiceLiveTelemetry). */
  telemetry: UseVoiceLiveTelemetryResult;
  /** Live WebSocket (owned by the connection layer) for the SDP offer/answer exchange. */
  wsRef: MutableRefObject<WebSocket | null>;
  /** ICE servers used when startAvatar is called without explicit servers. */
  avatarIceServers: RTCIceServer[];
  /** Whether ICE servers have been received (debug logging only). */
  hasIceServersRef: MutableRefObject<boolean>;
  /** Current video preference — drives whether a video transceiver is negotiated. */
  isVideoEnabledRef: MutableRefObject<boolean>;
  /** DEV-only: force the WebRTC avatar to fail to exercise the fallback path. */
  forceFailRef: MutableRefObject<boolean>;
  /** DEV-only: substitute bogus ICE servers to simulate a 701 ICE-stuck-in-checking failure. */
  forceIceTimeoutRef: MutableRefObject<boolean>;
  /** Tap an incoming audio MediaStream for the output meter. */
  tapOutputFromStream: (stream: MediaStream) => void;
  /** Set the user-facing error message. */
  setErrorMessage: (message: string) => void;
  /** The peer reached 'connected'. Reported synchronously so the machine's
   * `avatarActive` state is authoritative before any later failure arrives. */
  onAvatarActive: () => void;
  /**
   * Any avatar failure. The machine decides what it means from the `avatar`
   * region's current state (initial establishment vs. post-success drop).
   */
  onAvatarFailure: (reason: string) => void;
}

export interface UseAvatarPeerConnectionResult {
  /** WebRTC avatar lifecycle state (for rendering). */
  avatarState: AvatarState;
  /** Stale-closure-free mirror of {@link avatarState}. */
  avatarStateRef: MutableRefObject<AvatarState>;
  /** Set the avatar state — updates both the React state and the ref mirror. */
  setAvatarState: (state: AvatarState) => void;
  /** Ref for the `<video>` element that renders the avatar stream. */
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  /** Negotiate the WebRTC avatar peer using the given (or default) ICE servers. */
  startAvatar: (iceServers?: RTCIceServer[]) => Promise<void>;
  /** Tear down the peer connection + media elements and reset avatar state. */
  cleanupAvatar: () => void;
  /** Detach the video srcObject (audio-only toggle) without touching the peer. */
  detachVideo: () => void;
  /** Re-attach the stored video stream if present; returns whether it attached. */
  attachStoredVideo: () => boolean;
  /** Whether the active peer was negotiated with a video transceiver. */
  hasVideoTransceiver: () => boolean;
}

/**
 * Owns the WebRTC avatar peer connection: the RTCPeerConnection, the video /
 * remote-audio media elements, track wiring, connection/ICE state monitoring,
 * and the SDP offer/answer exchange over the WebSocket. The connection,
 * fallback-gating, and error state it depends on are injected so this hook
 * stays focused on the peer lifecycle.
 */
export const useAvatarPeerConnection = ({
  userId,
  telemetry,
  wsRef,
  avatarIceServers,
  hasIceServersRef,
  isVideoEnabledRef,
  forceFailRef,
  forceIceTimeoutRef,
  tapOutputFromStream,
  setErrorMessage,
  onAvatarActive,
  onAvatarFailure,
}: UseAvatarPeerConnectionOptions): UseAvatarPeerConnectionResult => {
  const [avatarState, setAvatarStateRaw] = useState<AvatarState>('inactive');
  const avatarStateRef = useRef<AvatarState>('inactive');
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Stores the avatar's video MediaStream so it can be re-attached when the
  // user toggles video back on without needing to reconnect the peer.
  const videoStreamRef = useRef<MediaStream | null>(null);
  // Tracks whether the current active peer connection was set up with a video
  // transceiver. Used to decide if a mode toggle requires peer reconnection.
  const peerConnectionHasVideoRef = useRef<boolean>(false);

  const { startIceConnectedTimeout, cancelIceConnectedTimeout } = useIceConnectedTimeout({
    userId,
    telemetry,
    pcRef,
    onAvatarFailure,
  });

  // Single source of truth for avatar state: keep the ref mirror in sync.
  const setAvatarState = (state: AvatarState) => {
    avatarStateRef.current = state;
    setAvatarStateRaw(state);
  };

  const cleanupAvatar = () => {
    cancelIceConnectedTimeout();
    // Close WebRTC connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    peerConnectionHasVideoRef.current = false;
    videoStreamRef.current = null;

    // Clear video — pause() must precede srcObject = null to settle any in-flight play() promise
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    // Remove and clear remote audio element
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.remove();
      remoteAudioRef.current = null;
    }

    setAvatarState('inactive');
  };

  /**
   * Detach the video srcObject so the browser stops decoding frames. The peer
   * connection and audio element stay alive — no reconnect, no audio gap.
   */
  const detachVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  };

  /**
   * Re-attach the stored avatar video stream (instant, no reconnect). Returns
   * true when a stored stream was attached, false when none is available.
   */
  const attachStoredVideo = (): boolean => {
    if (videoStreamRef.current && videoRef.current) {
      videoRef.current.srcObject = videoStreamRef.current;
      videoRef.current.play().catch((err) => {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Video re-enable play failed:', err);
        }
      });
      return true;
    }
    return false;
  };

  const hasVideoTransceiver = () => peerConnectionHasVideoRef.current;

  const startAvatar = async (iceServers?: RTCIceServer[]) => {
    // DEV-only: substitute unreachable ICE servers to simulate a 701-style
    // STUN/TURN failure where ICE gathering succeeds but connectivity checks stall.
    const serversToUse = forceIceTimeoutRef.current
      ? ([{ urls: 'stun:invalid.localhost:3478' }] as RTCIceServer[])
      : iceServers || avatarIceServers;

    console.log('startAvatar called - checking prerequisites:', {
      wsConnected: wsRef.current?.readyState === WebSocket.OPEN,
      hasIceServersRef: hasIceServersRef.current,
      avatarIceServersLength: avatarIceServers.length,
      providedServersLength: iceServers?.length || 0,
      serversToUseLength: serversToUse.length,
    });

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      setErrorMessage('Connection failed. Please check your internet connection.');
      return;
    }

    if (serversToUse.length === 0) {
      console.error('ICE servers not available:', {
        hasIceServersRef: hasIceServersRef.current,
        avatarIceServersLength: avatarIceServers.length,
        providedServersLength: iceServers?.length || 0,
      });
      setErrorMessage('Could not initialize avatar. Please try again.');
      return;
    }

    if (pcRef.current) {
      console.log('Avatar already connected');
      return;
    }

    setAvatarState('connecting');
    console.log('Initializing avatar connection...');

    // DEV-ONLY: force the WebRTC avatar to fail so the audio-only fallback path
    // can be exercised on a network that doesn't actually block STUN/TURN. Runs
    // through the same failAvatarEstablishment funnel as a real failure, so the
    // retry-then-fallback state machine is exercised end to end.
    if (forceFailRef.current) {
      console.warn('[DEV] Forcing avatar failure (force audio fallback toggle)');
      setAvatarState('failed');
      onAvatarFailure('forced-dev-fallback');
      return;
    }

    try {
      // Create RTCPeerConnection with ICE servers from Azure
      const pc = new RTCPeerConnection({
        bundlePolicy: 'max-bundle',
        iceServers: serversToUse,
      });
      pcRef.current = pc;

      // Add receive-only transceivers for avatar stream.
      // Video is only negotiated when the user has video enabled — skipping
      // the video transceiver in audio mode stops the server from sending
      // video, saving the full video stream bandwidth.
      pc.addTransceiver('audio', { direction: 'recvonly' });
      if (isVideoEnabledRef.current) {
        pc.addTransceiver('video', { direction: 'recvonly' });
      }
      peerConnectionHasVideoRef.current = isVideoEnabledRef.current;

      // Handle incoming tracks
      pc.ontrack = (event) => {
        console.log('🎬 Track received:', event.track.kind, 'from streams:', event.streams.length);

        if (event.track.kind === 'video') {
          console.log('📹 Video track details:', {
            id: event.track.id,
            label: event.track.label,
            readyState: event.track.readyState,
            muted: event.track.muted,
            enabled: event.track.enabled,
          });
          telemetry.trackDiagnostic('Avatar', 'VideoTrackReceived', {
            trackId: event.track.id,
            readyState: event.track.readyState,
            muted: event.track.muted,
            userId,
          });

          // Create new MediaStream with just the video track
          const videoStream = new MediaStream([event.track]);
          // Store so the toggle can re-attach without reconnecting the peer.
          videoStreamRef.current = videoStream;

          // Set video srcObject with retry logic
          const setVideoSource = () => {
            // Stop retrying if the peer connection was closed (e.g. component unmounted)
            if (!pcRef.current) return;
            if (videoRef.current) {
              console.log('Setting video srcObject');
              videoRef.current.srcObject = videoStream;
              videoRef.current
                .play()
                .then(() => {
                  console.log('✅ Video playing');
                  telemetry.trackDiagnostic('Avatar', 'VideoPlaying', { userId });
                })
                .catch((err) => {
                  // AbortError is expected when srcObject is cleared during cleanup — ignore it
                  if (err instanceof Error && err.name === 'AbortError') return;
                  console.error('❌ Video play failed:', err);
                  telemetry.trackException(err, { stage: 'videoPlay', userId });
                  alert('Click anywhere to enable video playback');
                  document.addEventListener(
                    'click',
                    () => {
                      videoRef.current?.play();
                    },
                    { once: true },
                  );
                });
            } else {
              console.warn('Video ref not ready, retrying in 100ms...');
              setTimeout(setVideoSource, 100);
            }
          };
          setVideoSource();
        }

        if (event.track.kind === 'audio') {
          console.log('🔊 Audio track details:', {
            id: event.track.id,
            label: event.track.label,
            readyState: event.track.readyState,
            muted: event.track.muted,
            enabled: event.track.enabled,
          });
          telemetry.trackDiagnostic('Avatar', 'AudioTrackReceived', {
            trackId: event.track.id,
            readyState: event.track.readyState,
            muted: event.track.muted,
            userId,
          });

          // Create new MediaStream with just the audio track
          const audioStream = new MediaStream([event.track]);

          // Tap the avatar's audio for the output meter (gated by the flag).
          tapOutputFromStream(audioStream);

          // Create hidden audio element for WebRTC audio (separate from video)
          let audioEl = remoteAudioRef.current;
          if (!audioEl) {
            audioEl = document.createElement('audio');
            audioEl.autoplay = true;
            audioEl.controls = false;
            audioEl.muted = false;
            audioEl.style.display = 'none';
            document.body.appendChild(audioEl);
            remoteAudioRef.current = audioEl;
            console.log('Created audio element');
          }
          audioEl.srcObject = audioStream;
          audioEl
            .play()
            .then(() => {
              console.log('✅ Audio playing');
              telemetry.trackDiagnostic('Avatar', 'AudioPlaying', { userId });
            })
            .catch((err) => {
              // AbortError is expected when srcObject is cleared during cleanup — ignore it
              if (err instanceof Error && err.name === 'AbortError') return;
              console.error('❌ Audio play failed:', err);
              telemetry.trackException(err, { stage: 'audioPlay', userId });
              alert('Click anywhere to enable audio playback');
              document.addEventListener(
                'click',
                () => {
                  audioEl?.play();
                },
                { once: true },
              );
            });
        }
      };

      // Monitor connection state
      pc.onconnectionstatechange = () => {
        // Ignore stale events from a peer that was intentionally replaced or
        // closed (e.g. after a voluntary mode switch). Without this guard,
        // closing a connecting peer triggers failure paths on the old closure.
        if (pcRef.current !== pc) return;
        console.log(`WebRTC connection state: ${pc.connectionState}`);
        telemetry.trackDiagnostic('Avatar', 'ConnectionStateChange', {
          state: pc.connectionState,
          userId,
        });
        if (pc.connectionState === 'connected') {
          cancelIceConnectedTimeout();
          setAvatarState('active');
          onAvatarActive();
        } else if (pc.connectionState === 'failed') {
          console.error('WebRTC connection failed');
          telemetry.trackException(new Error('WebRTC peer connection failed'), { userId });
          setAvatarState('failed');
          onAvatarFailure('pc-connection-failed');
        } else if (pc.connectionState === 'disconnected') {
          console.warn('WebRTC disconnected');
          setAvatarState('failed');
        }
      };

      pc.oniceconnectionstatechange = () => {
        // Ignore stale events from a peer that was intentionally replaced or
        // closed (e.g. after a voluntary mode switch).
        if (pcRef.current !== pc) return;
        console.log(`ICE connection state: ${pc.iceConnectionState}`);
        telemetry.trackDiagnostic('Avatar', 'IceConnectionStateChange', {
          state: pc.iceConnectionState,
          userId,
        });
        if (pc.iceConnectionState === 'failed') {
          console.error('ICE connection failed');
          telemetry.trackException(new Error('WebRTC ICE connection failed'), {
            iceServerUrls: telemetry.iceServerUrls(),
            userId,
          });
          onAvatarFailure('ice-connection-failed');
        }
      };

      // Wait for ICE gathering to complete (with timeout)
      const gatheringFinished = new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          const timeout = window.setTimeout(() => {
            console.warn('⚠️ ICE gathering timeout, proceeding anyway');
            telemetry.trackDiagnostic('Avatar', 'IceGatheringTimeout', {
              iceServerUrls: telemetry.iceServerUrls(),
              userId,
            });
            resolve();
          }, ICE_GATHERING_TIMEOUT);

          pc.addEventListener('icegatheringstatechange', () => {
            if (pc.iceGatheringState === 'complete') {
              window.clearTimeout(timeout);
              resolve();
            }
          });
        }
      });

      // Create and set local description
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for gathering with timeout - send offer even if not all candidates gathered
      await gatheringFinished;

      const localSdp = pc.localDescription?.sdp;
      if (!localSdp) {
        throw new Error('Failed to obtain local SDP');
      }

      console.log('Sending SDP offer to backend via WebSocket...');

      // Snapshot the WebSocket reference before entering the Promise executor.
      // wsRef.current may be nulled by cleanup() if the component unmounts
      // during the preceding async steps (createOffer / ICE gathering).
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket disconnected during avatar setup');
      }

      // Send SDP offer via WebSocket and wait for answer
      const answerPromise = new Promise<string>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          reject(new Error('Timeout waiting for avatar SDP answer'));
        }, SDP_ANSWER_TIMEOUT);

        const originalOnMessage = ws.onmessage;
        ws.onmessage = (event) => {
          const message = JSON.parse(event.data);
          if (message.type === 'avatar_answer' && message.sdp) {
            window.clearTimeout(timeout);
            ws.onmessage = originalOnMessage;
            resolve(message.sdp);
          } else if (message.type === 'avatar_error') {
            window.clearTimeout(timeout);
            ws.onmessage = originalOnMessage;
            reject(new Error(message.error || 'Avatar connection failed'));
          } else {
            // Pass other messages to original handler
            if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          }
        };
      });

      // Send offer
      ws.send(
        JSON.stringify({
          type: 'avatar_offer',
          sdp: localSdp,
        }),
      );

      // Wait for answer
      const serverSdp = await answerPromise;
      console.log('Received SDP answer from backend');

      // Set remote description with Azure's SDP answer
      await pc.setRemoteDescription({ type: 'answer', sdp: serverSdp });

      // Start the ICE connectivity-check deadline. If the peer doesn't reach
      // 'connected'/'completed' within ICE_CONNECTED_TIMEOUT ms, treat it as
      // an initial failure (covers the 701 "ICE stuck in checking" scenario).
      startIceConnectedTimeout(pc);

      console.log('Avatar connection established');
      // SDP exchange complete; ICE connectivity checks start next, so this is not yet 'active'.
      telemetry.trackDiagnostic('Avatar', 'Negotiated', {
        elapsedMs: telemetry.elapsedMs(),
        userId,
      });
    } catch (error) {
      cancelIceConnectedTimeout();
      console.error('Avatar connection error:', error);
      telemetry.trackException(error, { stage: 'startAvatar', userId });
      const errMsg = error instanceof Error ? error.message : String(error);
      setAvatarState('failed');
      // The peer is unusable either way; the machine decides what the failure means.
      cleanupAvatar();
      onAvatarFailure(`startAvatar-error:${errMsg}`);
    }
  };

  return {
    avatarState,
    avatarStateRef,
    setAvatarState,
    videoRef,
    startAvatar,
    cleanupAvatar,
    detachVideo,
    attachStoredVideo,
    hasVideoTransceiver,
  };
};
